'use client'

import { useMemo, useState } from 'react'

const QUESTIONS = [
  'We have a CRM where all of our leads and contacts live.',
  'Our lead data is centralized and clean — not scattered across spreadsheets.',
  'We have defined lead stages or a lifecycle (e.g. new → qualified → customer).',
  'We track where our leads come from (source / attribution).',
  'We send at least some automated emails today (welcome, follow-up, etc.).',
  'We have content to nurture leads — emails, guides, or resources.',
  'We get more leads than our team can personally follow up with quickly.',
  'Our core sales and marketing processes are documented and repeatable.',
  'We can measure conversion rates between each stage of our funnel.',
  'Leadership is bought in on investing in systems, not just more headcount.',
]

type Tier = {
  label: string
  range: string
  summary: string
  recommendation: string
  accent: string
}

function tierFor(score: number): Tier {
  if (score <= 3)
    return {
      label: 'Foundation Stage',
      range: '0–3',
      summary:
        'You’re early. The building blocks (clean data, a CRM, defined stages) need to come first before automation pays off.',
      recommendation:
        'Start by consolidating your lead data into a single CRM and defining your lifecycle stages. Get this right and automation becomes a force multiplier instead of automating chaos.',
      accent: 'border-warn/30 bg-warn/10',
    }
  if (score <= 6)
    return {
      label: 'Getting Ready',
      range: '4–6',
      summary:
        'You have real momentum and some pieces in place. A few gaps are holding back a reliable automation system.',
      recommendation:
        'Close the gaps — usually attribution, documented processes, or nurture content — then layer in automated follow-up. You’re close to unlocking meaningful leverage.',
      accent: 'border-accent/30 bg-accent/10',
    }
  if (score <= 8)
    return {
      label: 'Ready to Automate',
      range: '7–8',
      summary:
        'You have the foundations and the volume to benefit. Automation will likely deliver fast, measurable returns.',
      recommendation:
        'Prioritize the highest-leverage workflows first — instant lead response, lifecycle nurture, and CRM handoffs — and instrument everything so you can prove the lift.',
      accent: 'border-success/30 bg-success/10',
    }
  return {
    label: 'Primed to Scale',
    range: '9–10',
    summary:
      'You’re in the top tier. The opportunity now is sophistication — orchestration, personalization, and AI-driven workflows.',
    recommendation:
      'Move from individual automations to a connected system: cross-channel orchestration, lead scoring, and AI agents that handle judgment-light work end to end.',
    accent: 'border-success/30 bg-success/10',
  }
}

export default function ReadinessScorecard() {
  const [answers, setAnswers] = useState<boolean[]>(Array(QUESTIONS.length).fill(false))
  const [submitted, setSubmitted] = useState(false)

  const score = useMemo(() => answers.filter(Boolean).length, [answers])
  const tier = tierFor(score)

  function toggle(i: number) {
    setAnswers((prev) => prev.map((v, idx) => (idx === i ? !v : v)))
  }

  function reset() {
    setAnswers(Array(QUESTIONS.length).fill(false))
    setSubmitted(false)
  }

  return (
    <div className="mt-6 card p-8">
      <p className="text-sm text-muted">
        Check every statement that’s true for your business today.
      </p>
      <ul className="mt-6 flex flex-col gap-3">
        {QUESTIONS.map((q, i) => (
          <li key={q}>
            <label className="flex cursor-pointer items-start gap-3 card card-hover p-4 transition-colors hover:border-accent/40">
              <input
                type="checkbox"
                checked={answers[i]}
                onChange={() => toggle(i)}
                className="mt-1 h-4 w-4 shrink-0 accent-accent"
              />
              <span className="text-sm text-fg">{q}</span>
            </label>
          </li>
        ))}
      </ul>

      {!submitted ? (
        <button
          type="button"
          onClick={() => setSubmitted(true)}
          className="mt-6 inline-flex items-center justify-center rounded-md bg-accent px-7 py-3 text-base font-semibold text-accent-fg transition-colors hover:bg-accent-hover"
        >
          See my readiness score
        </button>
      ) : (
        <div className={`mt-8 rounded-md border p-6 ${tier.accent}`}>
          <div className="flex flex-col items-center text-center">
            <span className="text-xs uppercase tracking-widest text-muted">Your score</span>
            <span className="mt-1 text-4xl font-semibold text-fg">{score}/10</span>
            <span className="mt-2 text-lg font-semibold text-fg">{tier.label}</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-fg-secondary">{tier.summary}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            <strong className="text-fg">Recommended next step:</strong> {tier.recommendation}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={reset}
        className="mt-6 block text-sm text-accent underline-offset-4 hover:underline"
      >
        Start over
      </button>
    </div>
  )
}
